import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { Message } from 'primeng/message';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { FirebaseAuthService } from '@/cloud/services/firebase-auth.service';

/**
 * Native-app auth bridge. Opened by the iPad panel app inside an
 * `ASWebAuthenticationSession`, this page reuses the production Firebase login
 * (email/password, Google, Microsoft, SAML) and, on success, exchanges the Firebase
 * ID token for the internal JWT (the same `POST /api/auth/firebase/login` the web app uses),
 * then hands the session back to the app via the custom-scheme `redirect_uri`:
 *
 *   {redirect_uri}?result=<base64url(LoginResponse JSON)>      // success
 *   {redirect_uri}?error=<code>                                // failure
 *
 * The `redirect_uri` MUST use the `proavpanel://` scheme (validated below) so this page
 * can't be abused as an open redirect to exfiltrate tokens to an arbitrary origin.
 *
 * State is held in signals: the app is zoneless (`provideZonelessChangeDetection`), so
 * properties mutated after an `await` would otherwise not refresh the view.
 */
@Component({
    selector: 'app-mobile-auth',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, PasswordModule, Message],
    styles: [
        `
            :host {
                display: block;
            }
            .mobile-auth-card {
                border-radius: 32px;
                background: color-mix(in srgb, var(--p-surface-950) 55%, transparent);
                backdrop-filter: blur(10px);
            }
        `
    ],
    template: `
        <div class="bg-surface-950 flex items-center justify-center min-h-screen min-w-screen p-6">
            <div class="mobile-auth-card w-full max-w-md flex flex-col gap-4 py-12 px-8 sm:px-12">
                <div class="flex flex-col items-center text-center mb-2">
                    <svg width="165" height="38.343" style="margin-bottom: 1.25rem;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 165 38.343">
                        <g transform="translate(-192.662 -398.538)">
                            <g transform="translate(283.839 398.538)">
                                <path
                                    d="M305.921,428.649l13.742-23.8c.072-.121.139-.217.195-.294a2.422,2.422,0,0,1,.2.3l13.743,23.8h6.72L325.1,401.935a5.736,5.736,0,0,0-10.475,0L299.2,428.649Z"
                                    transform="translate(-299.199 -398.538)"
                                    style="fill: var(--p-primary-color, #7ab800)"
                                />
                            </g>
                            <g transform="translate(316.34 399.47)">
                                <path
                                    d="M371.776,399.628l-13.744,23.8a2.8,2.8,0,0,1-.195.3,2.782,2.782,0,0,1-.2-.3l-13.743-23.8h-6.72L352.6,426.342a5.736,5.736,0,0,0,10.475,0L378.5,399.628Z"
                                    transform="translate(-337.176 -399.628)"
                                    style="fill: var(--p-primary-color, #7ab800)"
                                />
                            </g>
                            <g transform="translate(192.662 398.671)">
                                <path
                                    d="M198.483,437.037h-5.821V409.521a9.911,9.911,0,0,1,9.9-9.9h8.053a14.608,14.608,0,0,1,0,29.216h-8.538v-5.826h8.538a8.782,8.782,0,0,0,0-17.564h-8.053a4.081,4.081,0,0,0-4.075,4.078v27.516Z"
                                    transform="translate(-192.662 -398.828)"
                                    fill="#868686"
                                />
                                <g transform="translate(35.513 0.789)">
                                    <path
                                        d="M259.588,402.722a14.5,14.5,0,0,0-9-3.1h-6.5a9.909,9.909,0,0,0-9.9,9.9V428.8h5.821V409.515a4.081,4.081,0,0,1,4.076-4.075h6.5a8.714,8.714,0,0,1,5.979,2.363A18.432,18.432,0,0,1,259.588,402.722Z"
                                        transform="translate(-234.193 -399.617)"
                                        fill="#868686"
                                    />
                                </g>
                                <path
                                    d="M279.622,429.48c-8.972,0-16.268-6.906-16.268-15.392s7.3-15.393,16.268-15.393,16.27,6.9,16.27,15.393-7.3,15.392-16.27,15.392Zm0-24.965c-5.76,0-10.446,4.294-10.446,9.573s4.686,9.573,10.446,9.573,10.448-4.3,10.448-9.573-4.688-9.573-10.448-9.573Z"
                                    transform="translate(-202.906 -398.694)"
                                    fill="#868686"
                                />
                            </g>
                        </g>
                    </svg>
                    <h1 class="text-surface-0 text-xl font-semibold">Sign in to proAV Cloud</h1>
                    <p class="text-surface-400 text-sm mt-1">Remote access for your panel</p>
                </div>

                @if (configError()) {
                    <p-message severity="error">{{ configError()! }}</p-message>
                } @else {
                    @if (error()) {
                        <p-message severity="error">{{ error()! }}</p-message>
                    }

                    @if (step() === 'email') {
                        <input pInputText type="email" placeholder="Email" [(ngModel)]="email" class="w-full" autocomplete="username" autocapitalize="none" autocorrect="off" (keyup.enter)="continueEmail()" [disabled]="busy()" />
                        <p-button label="Continue" (onClick)="continueEmail()" [loading]="busy()" [disabled]="!email" class="w-full" />
                    } @else {
                        <input pInputText type="email" [value]="email" disabled class="w-full" />
                        <p-password [(ngModel)]="password" [feedback]="false" [toggleMask]="true" placeholder="Password" class="w-full" inputStyleClass="w-full" (keyup.enter)="submitPassword()" [disabled]="busy()" />
                        <p-button label="Sign In" (onClick)="submitPassword()" [loading]="busy()" [disabled]="!password" class="w-full" />
                        <p-button label="Back" (onClick)="resetToEmail()" [text]="true" severity="secondary" class="w-full" [disabled]="busy()" />
                    }

                    @if ((googleEnabled() || microsoftEnabled()) && step() === 'email') {
                        <div class="flex items-center gap-3 my-1">
                            <span class="flex-1 h-px bg-surface-700"></span>
                            <span class="text-surface-500 text-xs">or</span>
                            <span class="flex-1 h-px bg-surface-700"></span>
                        </div>
                        @if (googleEnabled()) {
                            <p-button label="Continue with Google" icon="pi pi-google" severity="secondary" (onClick)="loginGoogle()" [disabled]="busy()" class="w-full" />
                        }
                        @if (microsoftEnabled()) {
                            <p-button label="Continue with Microsoft" icon="pi pi-microsoft" severity="secondary" (onClick)="loginMicrosoft()" [disabled]="busy()" class="w-full" />
                        }
                    }
                }
            </div>
        </div>
    `
})
export class MobileAuth implements OnInit {
    private route = inject(ActivatedRoute);
    private firebaseAuth = inject(FirebaseAuthService);
    private cloud = inject(CloudApiService);

    private static readonly redirectUriKey = 'proav.mobileAuth.redirectUri';

    private redirectUri = '';
    protected configError = signal<string | null>(null);
    protected error = signal<string | null>(null);
    protected busy = signal(false);
    protected step = signal<'email' | 'password'>('email');
    protected email = '';
    protected password = '';

    protected googleEnabled = computed(() => this.firebaseAuth.isGoogleEnabled());
    protected microsoftEnabled = computed(() => this.firebaseAuth.isMicrosoftEnabled());

    ngOnInit(): void {
        void this.initialise();
    }

    private async initialise(): Promise<void> {
        // A provider redirect returns to this page without the original query string, so the
        // validated redirect_uri is stashed for the second load.
        const fromQuery = this.route.snapshot.queryParamMap.get('redirect_uri') ?? '';
        const redirect = fromQuery || this.storedRedirectUri();

        // Only allow the native app's custom scheme — never an arbitrary origin. Checked
        // again on the stored value, so a poisoned store cannot widen it.
        if (!redirect.startsWith('proavpanel://')) {
            this.configError.set('This sign-in page can only be opened from the proAV Panel app.');
            return;
        }
        this.redirectUri = redirect;
        if (fromQuery) {
            this.storeRedirectUri(fromQuery);
        }

        // Returning from a provider redirect? Resolves to null on a normal first load.
        this.busy.set(true);
        try {
            const user = await this.firebaseAuth.completeRedirectSignIn();
            if (user) {
                await this.finish();
            }
        } catch (err: any) {
            this.error.set(this.messageFor(err));
        } finally {
            this.busy.set(false);
        }
    }

    protected async continueEmail(): Promise<void> {
        const email = this.email.trim();
        if (!email || this.busy()) return;
        this.busy.set(true);
        this.error.set(null);
        try {
            // Domain-routed SAML. Deliberately NOT gated on isSamlEnabled(): that reflects
            // Firebase:EnabledProviders, which lists no saml.* entry in production, whereas
            // the domain->provider mapping lives in Firebase:SamlDomains and is only known to
            // /auth/check-domain. Gating here sent SAML users to a password field that cannot
            // work. This matches the web login (cloud-login.ts).
            const method = await this.cloud.checkAuthMethodForEmail(email).catch(() => null);
            if (method?.authMethod === 'saml' && method.provider) {
                // Redirect, not popup: this page runs inside ASWebAuthenticationSession,
                // where signInWithPopup always fails with auth/popup-blocked. The IdP
                // returns to this page and `initialise` completes the sign-in.
                await this.firebaseAuth.signInWithSAMLRedirect(method.provider);
                return;
            }
            this.step.set('password');
        } catch (err: any) {
            this.error.set(this.messageFor(err));
        } finally {
            this.busy.set(false);
        }
    }

    protected submitPassword(): void {
        if (!this.password || this.busy()) return;
        void this.run(() => this.firebaseAuth.signInWithEmailAndPassword(this.email.trim(), this.password));
    }

    protected loginGoogle(): void {
        void this.startRedirect(() => this.firebaseAuth.signInWithGoogleRedirect());
    }

    protected loginMicrosoft(): void {
        void this.startRedirect(() => this.firebaseAuth.signInWithMicrosoftRedirect());
    }

    /**
     * Begin a redirect sign-in. On success the browser navigates away, so `busy` is left
     * set — it is only cleared if starting the redirect itself failed.
     */
    private async startRedirect(begin: () => Promise<void>): Promise<void> {
        if (this.busy()) return;
        this.busy.set(true);
        this.error.set(null);
        try {
            await begin();
        } catch (err: any) {
            this.error.set(this.messageFor(err));
            this.busy.set(false);
        }
    }

    protected resetToEmail(): void {
        this.step.set('email');
        this.password = '';
        this.error.set(null);
    }

    /** Run a Firebase sign-in then hand the session back to the app. */
    private async run(signIn: () => Promise<unknown>): Promise<void> {
        this.busy.set(true);
        this.error.set(null);
        try {
            await signIn();
            await this.finish();
        } catch (err: any) {
            this.error.set(this.messageFor(err));
        } finally {
            this.busy.set(false);
        }
    }

    /** Exchange the Firebase ID token for the internal JWT and redirect to the app. */
    private async finish(): Promise<void> {
        const idToken = await this.firebaseAuth.getIdToken();
        if (!idToken) {
            throw new Error('Could not obtain a sign-in token. Please try again.');
        }
        const res = await this.cloud.loginWithFirebaseToken(idToken, true);
        this.handOff(res);
    }

    private handOff(loginResponse: unknown): void {
        this.clearStoredRedirectUri();
        const payload = this.base64url(JSON.stringify(loginResponse));
        window.location.href = `${this.redirectUri}?result=${payload}`;
    }

    // The redirect_uri has to survive the IdP round-trip. sessionStorage is scoped to this
    // browsing session, which is ephemeral for the panel app, and every read is re-validated
    // against the proavpanel:// scheme above. All three are guarded: storage throws in some
    // privacy modes, and a bridge that cannot remember the URI is still usable on first load.
    private storedRedirectUri(): string {
        try {
            return sessionStorage.getItem(MobileAuth.redirectUriKey) ?? '';
        } catch {
            return '';
        }
    }

    private storeRedirectUri(value: string): void {
        try {
            sessionStorage.setItem(MobileAuth.redirectUriKey, value);
        } catch {
            // Non-fatal: only the redirect round-trip needs it.
        }
    }

    private clearStoredRedirectUri(): void {
        try {
            sessionStorage.removeItem(MobileAuth.redirectUriKey);
        } catch {
            // Non-fatal.
        }
    }

    private messageFor(err: any): string {
        return err?.message || err?.error || 'Sign-in failed. Please try again.';
    }

    private base64url(value: string): string {
        const b64 = btoa(unescape(encodeURIComponent(value)));
        return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
}
